import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  getAuth,
  signInWithPopup,
  onIdTokenChanged,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  getIdToken,
  getIdTokenResult,
  connectAuthEmulator,
} from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { COLLECTIONS } from 'constants/firestoreCollections';
import { useFirestore } from 'utils/firebase';
import { apiClient } from 'api-client';

// Module-level flag — prevents double-connection on Vite hot reload.
// connectAuthEmulator throws if called more than once per instance.
const emulatorConnectedApps = new Set();

const googleAuthProvider = new GoogleAuthProvider();
googleAuthProvider.addScope('https://www.googleapis.com/auth/calendar.events');

const defaultSession = { loading: true };

const createUser = _user => {
  const userKeys = 'displayName,email,emailVerified,isAnonymous,phoneNumber,photoURL,uid'.split(
    ',',
  );

  const user = userKeys.reduce(
    (prev, key) => ({
      ...prev,
      [key]: _user[key] === null ? undefined : _user[key],
    }),
    {},
  );

  const providerId = user.isAnonymous
    ? 'anonymous'
    : (_user.providerData[0]?.providerId ?? 'custom');

  user.authProvider = providerId;

  // Convert to Date objects
  const { creationTime, lastSignInTime } = _user.metadata;
  user.creationTime = new Date(creationTime);
  user.lastSignInTime = new Date(lastSignInTime);

  // Build a fake username
  if (!user.displayName) {
    user.displayName = `USER_${user.uid.substr(0, 6)}`;
  }
  return user;
};

const getUserDatabase = async ({ uid, ...rest }, database) => {
  const userRef = doc(database, COLLECTIONS.USERS, uid);
  const userSnap = await getDoc(userRef);

  return { uid, ...rest, ...userSnap.data(), _exists: userSnap.exists() };
};

const loadUserFromDatabase = async (uid, database) => {
  const userRef = doc(database, COLLECTIONS.USERS, uid);
  const userSnap = await getDoc(userRef);

  const user = userSnap.data();

  return userSnap.exists() ? { ...user, uid: userSnap.id } : {};
};

const useFirebaseAuth = firebaseApp => {
  const [session, setSession] = useState(defaultSession);
  const [signInError, setSignInError] = useState(null);
  const [tenant, setTenant] = useState(null);
  const database = useFirestore();
  const firebaseUserRef = useRef(null);

  const auth = useMemo(() => getAuth(firebaseApp), [firebaseApp]);

  if (import.meta.env.VITE_ENV === 'local' && !emulatorConnectedApps.has(firebaseApp)) {
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    emulatorConnectedApps.add(firebaseApp);
  }

  const onSignInError = useCallback(
    error => {
      setSignInError(error);
    },
    [setSignInError],
  );

  const signIn = useCallback(() => {
    setSignInError(null);
    return signInWithPopup(auth, googleAuthProvider).catch(onSignInError);
  }, [auth, setSignInError, onSignInError]);

  const signOut = useCallback(() => firebaseSignOut(auth), [auth]);

  const updateUserTenant = useCallback(
    async (uid, tenant) => {
      await new Promise((resolve, reject) => {
        let unsub;

        const timer = setTimeout(() => {
          unsub?.();
          reject(new Error('Tenant sync timed out. Please try again.'));
        }, 30000);

        unsub = onIdTokenChanged(
          auth,
          async newUser => {
            if (!newUser) return;

            const { claims } = await newUser.getIdTokenResult();
            if (claims.tenantId === tenant.id) {
              clearTimeout(timer);
              unsub();
              resolve();
            }
          },
          err => {
            clearTimeout(timer);
            unsub?.();
            reject(err);
          },
        );

        apiClient('POST', `/users/${uid}/tenant`, () => getIdToken(firebaseUserRef.current), {
          tenantId: tenant.id,
        })
          .then(result => {
            if (result?.status !== 'OK') {
              clearTimeout(timer);
              unsub?.();
              reject(new Error(result?.message || 'Tenant assignment failed.'));
            }
          })
          .catch(err => {
            clearTimeout(timer);
            unsub?.();
            reject(err);
          });
      });
    },
    [auth],
  );

  // Watches claimsSyncedAt on the current user's doc and force-refreshes the token
  // whenever it advances — covers role changes taking effect immediately.
  useEffect(() => {
    const uid = session._user?.uid;
    if (!uid) return;

    const userRef = doc(database, COLLECTIONS.USERS, uid);
    let lastSyncedAt = null;

    return onSnapshot(userRef, snap => {
      const syncedAt = snap.data()?.claimsSyncedAt?.seconds ?? 0;
      if (lastSyncedAt === null) {
        lastSyncedAt = syncedAt;
        return;
      }
      if (syncedAt > lastSyncedAt) {
        lastSyncedAt = syncedAt;
        firebaseUserRef.current?.getIdToken(true);
      }
    });
  }, [database, session._user?.uid]);

  useEffect(
    () =>
      onIdTokenChanged(auth, async _user => {
        firebaseUserRef.current = _user ?? null;
        const isSignedIn = !!_user;
        let user = isSignedIn ? createUser(_user) : {};
        // User is signed in or token was refreshed.
        if (isSignedIn) {
          user = await getUserDatabase(user, database);
          if (!user._exists) {
            let result;
            try {
              result = await apiClient('POST', '/users', () => _user.getIdToken());
            } catch {
              result = null;
            }
            if (result?.status !== 'OK') {
              setSignInError(new Error(result?.message || 'Failed to set up your account.'));
              setSession({ isSignedIn: false, _user: null });
              firebaseSignOut(auth).catch(() => {});
              return;
            }
          }
          user = await loadUserFromDatabase(user.uid, database);
        }
        const hasTenant = user.tenant || false;
        if (hasTenant) {
          const tenantRef = doc(database, COLLECTIONS.TENANTS, user.tenant.id);
          const userSnap = await getDoc(tenantRef);

          const tenant = userSnap.data();
          setTenant(tenant);
        }
        let isManager = false;
        let isHardwareManager = false;
        let isOfficeManager = false;
        if (isSignedIn) {
          const { claims } = await getIdTokenResult(_user);
          isManager = Boolean(claims.officeManager || claims.manager || claims.accountManager);
          isHardwareManager = Boolean(claims.hardwareManager);
          isOfficeManager = Boolean(claims.officeManager);
        }
        setSession({
          isSignedIn,
          hasTenant,
          isManager,
          isHardwareManager,
          isOfficeManager,
          user,
          _user,
        });
      }),
    [auth, setSession, database, setSignInError],
  );

  const getToken = useCallback((forceRefresh = false) => {
    if (!firebaseUserRef.current) throw new Error('Not authenticated');
    return getIdToken(firebaseUserRef.current, forceRefresh);
  }, []);

  const results = useMemo(
    () => ({
      ...session,
      signInError,
      firebaseApp,
      signIn,
      signOut,
      updateUserTenant,
      getToken,
      tenant,
    }),
    [session, signInError, firebaseApp, signIn, signOut, updateUserTenant, getToken, tenant],
  );

  return results;
};

export default useFirebaseAuth;
